import { createAction } from '../../store.model';
import { User } from '../../user/user.model';
import { GridType } from '../core/template-definition.model';
import {
    PeriodType,
    ScorecardBoxDto,
    ScorecardDataDto,
} from '../scorecard.model';

import { GridTab } from './grid-tab.model';

export enum ActionType {
    SCORECARD__CREATE_TAB = 'SCORECARD__CREATE_TAB',
    SCORECARD__CREATE_TAB_RESULT = 'SCORECARD__CREATE_TAB_RESULT',
    SCORECARD__REMOVE_TAB = 'SCORECARD__REMOVE_TAB',
    SCORECARD__SELECT_TAB = 'SCORECARD__SELECT_TAB',
    SCORECARD__RELOAD = 'SCORECARD__RELOAD', // TODO maybe rename
    SCORECARD__RELOAD_RESULT = 'SCORECARD__RELOAD_RESULT',
    SCORECARD__RELOAD_CANCEL = 'SCORECARD__RELOAD_CANCEL',
    SCORECARD__LIST_DATA = 'SCORECARD__LIST_DATA',
    SCORECARD__LIST_DATA_RESULT = 'SCORECARD__LIST_DATA_RESULT',
    SCORECARD__LIST_DATA_CANCEL = 'SCORECARD__LIST_DATA_CANCEL',
    SCORECARD__SELECT_PERIOD_TYPE = 'SCORECARD__SELECT_PERIOD_TYPE',
    SCORECARD__SELECT = 'SCORECARD__SELECT',
    SCORECARD__EXPORT = 'SCORECARD__EXPORT',
    SCORECARD__EXPORT_RESULT = 'SCORECARD__EXPORT_RESULT',
    SCORECARD__RESET = 'SCORECARD__RESET',
    SCORECARD__RESET_GRID = 'SCORECARD__RESET_GRID',
    SCORECARD__WIDGETS_EXPORT = 'SCORECARD__WIDGETS_EXPORT',
    SCORECARD__WIDGETS_EXPORT_RESULT = 'SCORECARD__WIDGETS_EXPORT_RESULT',
    SCORECARD__SELECT_GRID_TAB = 'SELECTED_GRID_TAB',
    SCORECARD__SELECT_DIRECT_REPORT_DROPDOWN = 'SCORECARD__SELECT_DIRECT_REPORT_DROPDOWN',
}

export interface CreateTab {
    tabId: string;

    /** if userId is specified, user is ignored */
    userId?: number;

    /** if userId is specified, user is ignored */
    user?: User;

    authUserId: number;
    defaultSelectedSCId?: number;
}

export const createTab = createAction<ActionType, CreateTab>(
    ActionType.SCORECARD__CREATE_TAB,
);

export interface CreateTabResult extends CreateTab {}

export const createTabResult = createAction<ActionType, CreateTabResult>(
    ActionType.SCORECARD__CREATE_TAB_RESULT,
);

/// ///////

export interface RemoveTab {
    tabId: string;
}

export const removeTab = createAction<ActionType, RemoveTab>(
    ActionType.SCORECARD__REMOVE_TAB,
);

/// ///////

export interface SelectTab {
    tabId: string;
}

export const selectTab = createAction<ActionType, SelectTab>(
    ActionType.SCORECARD__SELECT_TAB,
);

/// ///////

export interface Reload {
    tabId: string;
    periodTypes?: PeriodType[]; // TODO maybe api or db-stored-proc can one day change to accept multiple period types

    /** override the default initial scorecard */
    scorecardId?: number;

    /** override the default initial scorecard by the latest occurrence of a groupId.
     * this is attempted if scorecardId is unspecified or not found */
    groupId?: number;

    /** override the default initial scorecard.
     * does a year and month match against the end dates of the specified user's scorecard history.
     * this is attempted if scorecardId is unspecified or not found */
    endDate?: Date;

    /** this only needs to be set when the intent is to switch modes.
     * dispatches of this action where this property is null/undefined will
     * use the current value in state.tabs[tabId].useLiveData */
    useLiveData?: boolean;

    /** set to true if the tab exists with existing data and selections,
     * and a reset of that data and selections are wanted */
    hard?: boolean;
}

export const reload = createAction<ActionType, Reload>(
    ActionType.SCORECARD__RELOAD,
);

export interface ReloadResult extends Reload {
    scorecardBoxes?: ScorecardBoxDto[];
}

export const reloadResult = createAction<ActionType, ReloadResult>(
    ActionType.SCORECARD__RELOAD_RESULT,
);

export interface ReloadCancel {
    tabId: string;
}

export const reloadCancel = createAction<ActionType, ReloadCancel>(
    ActionType.SCORECARD__RELOAD_CANCEL,
);

/// ///////

export interface ListData {
    tabId: string;
    scorecardIds: number[];
    useLiveData?: boolean;
}

export const listData = createAction<ActionType, ListData>(
    ActionType.SCORECARD__LIST_DATA,
);

export interface ListDataResult extends ListData {
    scorecardData?: ScorecardDataDto[];
}

export const listDataResult = createAction<ActionType, ListDataResult>(
    ActionType.SCORECARD__LIST_DATA_RESULT,
);

export interface ListDataCancel {
    tabId: string;
}

export const listDataCancel = createAction<ActionType, ListDataCancel>(
    ActionType.SCORECARD__LIST_DATA_CANCEL,
);

/// ///////

export interface SelectPeriodType {
    tabId: string;
    periodType: PeriodType;
}

export const selectPeriodType = createAction<ActionType, SelectPeriodType>(
    ActionType.SCORECARD__SELECT_PERIOD_TYPE,
);

/// ///////

export interface SelectScorecard {
    scorecardId: number;
}

export const selectScorecard = createAction<ActionType, SelectScorecard>(
    ActionType.SCORECARD__SELECT,
);

/// ///////

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Export {}

export const exportScorecard = createAction<ActionType, Export>(
    ActionType.SCORECARD__EXPORT,
);

export interface ExportResult extends Export {}

export const exportScorecardResult = createAction<ActionType, ExportResult>(
    ActionType.SCORECARD__EXPORT_RESULT,
);

/// ///////

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Reset {}

/** TODO revise. this is temporary means of flushing tabs after auth user has changed */
export const reset = createAction<ActionType, Reset>(
    ActionType.SCORECARD__RESET,
);

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ResetGrid {}
export const resetGrid = createAction<ActionType, ResetGrid>(
    ActionType.SCORECARD__RESET_GRID,
);

export interface WidgetsExport {
    activeDrptTab: string;
}
export const widgetsExport = createAction<ActionType, WidgetsExport>(
    ActionType.SCORECARD__WIDGETS_EXPORT,
);
export const widgetsExportResult = createAction<ActionType, {}>(
    ActionType.SCORECARD__WIDGETS_EXPORT_RESULT,
);

export interface SelectedGridTab {
    selectedGridTab: GridTab;
}

export const selectedGridTab = createAction<ActionType, SelectedGridTab>(
    ActionType.SCORECARD__SELECT_GRID_TAB,
);

export interface SelectedDirectReportOption {
    selectedDirectReportOption: GridType;
}

export const selectedDirectReportOption = createAction<
    ActionType,
    SelectedDirectReportOption
>(ActionType.SCORECARD__SELECT_DIRECT_REPORT_DROPDOWN);
